/**
 * Global quantity selector web component.
 *
 * Usage:
 * This component is designed to manage the quantity of items for products on an e-commerce platform.
 * It listens to user interactions to increment or decrement the quantity value.
 * Ensure to provide the necessary HTML structure when deploying this component.
 *
 * Example HTML markup:
 *
 * <uwp-quantity-selector>
 *   <div class="some-class">
 *     <input type="button" value="-" class="minus">
 *     <input type="number" step="1" min="1" readonly max="10" name="quantity" value="1" title="Qty" class="quantity" size="4">
 *     <input type="button" value="+" class="plus">
 *   </div>
 * </uwp-quantity-selector>
 *
 */

class QuantitySelector extends HTMLElement {
  constructor() {
    super();
  }

  state = {
    showingErrorMessage: false,
  };

  connectedCallback() {
    const minusButton = this.querySelector(".minus");
    const plusButton = this.querySelector(".plus");
    const quantityInput = this.querySelector(".quantity") as HTMLInputElement;

    minusButton?.addEventListener("click", () =>
      this.adjustQuantity(quantityInput, -1),
    );
    plusButton?.addEventListener("click", () =>
      this.adjustQuantity(quantityInput, 1),
    );
  }

  private adjustQuantity(input: HTMLInputElement, adjustment: number) {
    const currentVal = parseInt(input.value, 10);
    const newCount = currentVal + adjustment;
    const max = parseInt(input.max, 10);
    const min = parseInt(input.min, 10);

    if (newCount >= min && newCount <= max) {
      input.value = newCount.toString();
      return;
    }

    if (
      currentVal === max &&
      adjustment > 0 &&
      !this.state.showingErrorMessage
    ) {
      const swatchGroup = this.closest(".product-swatches__form-group");
      if (!swatchGroup) return;

      swatchGroup.classList.add("product-swatches__form-group--max-reached");

      const maxError = document.createElement("span");
      maxError.classList.add("product-swatches__max-qty-error");
      maxError.innerHTML = "You've reached the max quantity";

      maxError.style.display = "block";
      maxError.style.color = "red";
      maxError.style.fontWeight = "bold";
      maxError.style.marginTop = "10px";
      maxError.style.marginBottom = "10px";
      maxError.style.position = "relative";
      maxError.style.zIndex = "10";

      swatchGroup.insertAdjacentElement("afterend", maxError);

      this.state.showingErrorMessage = true;
      setTimeout(() => {
        swatchGroup.classList.remove(
          "product-swatches__form-group--max-reached",
        );
        maxError.remove();
        this.state.showingErrorMessage = false;
      }, 3000);
    }

    this.dispatchEvent(
      new CustomEvent("quantityChanged", { detail: newCount, bubbles: true }),
    );
  }
}

customElements.define("uwp-quantity-selector", QuantitySelector);
